import axios from 'axios';

export type AuthSession = {
  id: string;
  email?: string;
  emailVerified: boolean;
  provider: string;
  isGuest: boolean;
  mergedGuestData?: boolean;
};

const apiBase = process.env.REACT_APP_API_URL ?? '';

async function readAuthError(error: unknown, fallback: string): Promise<string> {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.error;
    if (typeof message === 'string') {
      return message;
    }
  }
  return fallback;
}

export async function fetchAuthSession(): Promise<AuthSession | null> {
  try {
    const { data, status } = await axios.get<AuthSession>(`${apiBase}/auth/me`, {
      withCredentials: true,
      validateStatus: (s) => s === 200 || s === 401,
    });
    return status === 200 ? data : null;
  } catch {
    return null;
  }
}

export async function createGuestSession(): Promise<AuthSession> {
  const { data } = await axios.post<AuthSession>(
    `${apiBase}/auth/guest`,
    {},
    { withCredentials: true },
  );
  return data;
}

export async function loginWithEmail(
  email: string,
  password: string,
): Promise<AuthSession> {
  try {
    const { data } = await axios.post<AuthSession>(
      `${apiBase}/auth/login`,
      { email, password },
      { withCredentials: true },
    );
    return data;
  } catch (error) {
    throw new Error(await readAuthError(error, 'Unable to sign in. Please try again.'));
  }
}

export async function registerWithEmail(
  email: string,
  password: string,
): Promise<AuthSession & { message?: string }> {
  try {
    const { data } = await axios.post<AuthSession & { message?: string }>(
      `${apiBase}/auth/register`,
      { email, password },
      { withCredentials: true },
    );
    return data;
  } catch (error) {
    throw new Error(await readAuthError(error, 'Unable to create account.'));
  }
}

export async function resendVerificationEmail(): Promise<void> {
  try {
    await axios.post(
      `${apiBase}/auth/resend-verification`,
      {},
      { withCredentials: true },
    );
  } catch (error) {
    throw new Error(
      await readAuthError(error, 'Unable to resend verification email.'),
    );
  }
}

export async function verifyEmailWithCode(
  code: string,
): Promise<{ emailVerified: boolean }> {
  try {
    const { data } = await axios.post<{ emailVerified: boolean }>(
      `${apiBase}/auth/verify-email`,
      { code },
      { withCredentials: true },
    );
    return data;
  } catch (error) {
    throw new Error(
      await readAuthError(error, 'Unable to verify email.'),
    );
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  try {
    await axios.post(`${apiBase}/auth/forgot-password`, { email });
  } catch (error) {
    throw new Error(
      await readAuthError(error, 'Unable to send password reset email.'),
    );
  }
}

export async function resetPassword(
  code: string,
  password: string,
): Promise<void> {
  try {
    await axios.post(`${apiBase}/auth/reset-password`, { code, password });
  } catch (error) {
    throw new Error(await readAuthError(error, 'Unable to reset password.'));
  }
}

export async function logoutSession(): Promise<void> {
  await axios.delete(`${apiBase}/auth/cookie`, { withCredentials: true });
}
